import { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import { LinkButton } from '@atlaskit/button/new';
import { useFlags } from '@atlaskit/flag';
import Heading from '@atlaskit/heading';
import ErrorIcon from '@atlaskit/icon/core/error';
import SuccessIcon from '@atlaskit/icon/core/success';
import Link from '@atlaskit/link';
import { Box, Flex, Stack, Text, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import Toggle from '@atlaskit/toggle';
import { useTenantData } from '@dev-agents/tenant-context';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { PageSection } from '../../../../common/components/page-section';
import { fg } from '../../../../controllers/feature-gates';
import { useRovoDevWebAnalyticsClient } from '../../../../controllers/rovo-dev-web-analytics-events';
import { settingsRovoDevCLIPath } from '../../../../routes/paths';
import { JOIN_WAITLIST_LINK, ROVO_DEV_CLI_READ_MORE_URL } from '../../../../utils/constants';
import { RovoDevAgentsPageHeader } from '../../components/rovo-dev-agents-page-header';
import { useGetRovoDevCLISettings } from '../hooks/use-get-rovo-dev-cli-settings-controller';
import { useSetRovoDevCLISettings } from '../hooks/use-set-rovo-dev-cli-settings-controller';
import { buildDevAiWorkspaceAri } from '../utils';

import messages from './messages';
import { RovoDevCLIInstructions } from './rovo-dev-cli-instructions';
import { RovoDevCLISettingsWaitlistImage } from './rovo-dev-cli-settings-waitlist-image';

export const RovoDevCLISettings = () => {
	const [isEnabled, setIsEnabled] = useState<boolean>();
	const { showFlag } = useFlags();
	const { setRovoDevCLISettings } = useSetRovoDevCLISettings();
	const { loading, error, getRovoDevCLISettings } = useGetRovoDevCLISettings();
	const { tenantContexts } = useTenantData();
	const activationId = tenantContexts[0].devAiActivationId;
	const { fireUiEvent } = useRovoDevWebAnalyticsClient();
	const { formatMessage } = useIntl();

	useEffect(() => {
		if (!activationId) return;
		getRovoDevCLISettings(buildDevAiWorkspaceAri(activationId)).then((settings) => {
			const isEnabled = settings?.value?.value?.rovo_dev_cli_enabled || false;
			setIsEnabled(isEnabled);
		});
	}, [activationId, getRovoDevCLISettings, tenantContexts]);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (!activationId) return;
		const isChecked = event.target.checked;
		setIsEnabled(isChecked);
		setRovoDevCLISettings({
			containerId: buildDevAiWorkspaceAri(activationId),
			value: isChecked,
		}).then((settings) => {
			if (settings === null) {
				setIsEnabled(!isChecked);
				showFlag({
					id: 'rovo-dev-cli-enabled-failed',
					title: formatMessage(messages.errorNotificationTitle),
					description: error || 'Something went wrong.',
					icon: <ErrorIcon label="Rovo Dev CLI setting failed" />,
					appearance: 'error',
					isAutoDismiss: true,
				});
			}

			if (settings !== null) {
				fireUiEvent({
					action: 'clicked',
					actionSubject: 'rovo-dev-cli-setting-toggle',
					actionSubjectId: 'rovo-dev-cli-setting-toggle',
					source: 'rovo-dev-cli-setting',
					attributes: {
						isChecked,
					},
				});

				if (isChecked) {
					const dismissFlag = showFlag({
						id: 'rovo-dev-cli-enabled-success',
						title: formatMessage(messages.successNotificationTitle),
						description: formatMessage(messages.successNotificationDescription),
						icon: <SuccessIcon label="Rovo Dev CLI toggle enabled" />,
						appearance: 'success',
						actions: [
							{
								content: formatMessage(messages.dismissFlagAckButtonTitle),
								onClick: () => dismissFlag(),
							},
							{
								content: formatMessage(messages.dismissFlagLearnMoreButtonTitle),
								href: ROVO_DEV_CLI_READ_MORE_URL,
								target: '_blank',
							},
						],
					});
				}
			}
		});
	};

	const getDescription = () => {
		return (
			<Text>
				{formatMessage(messages.pageDescription)}{' '}
				<Link href={ROVO_DEV_CLI_READ_MORE_URL} target="_blank">
					{formatMessage(messages.learnMoreLinkTitle)}
				</Link>
			</Text>
		);
	};

	return (
		<PageSection>
			<Stack space="space.400">
				<RovoDevAgentsPageHeader
					additionalBreadcrumbItems={[
						<BreadcrumbsItem
							href={`/dev-agents${settingsRovoDevCLIPath}`}
							text={formatMessage(messages.breadCrumbTitle)}
							key="rovo-dev-cli"
						/>,
					]}
					headingText={formatMessage(messages.breadCrumbTitle)}
					description={getDescription()}
				/>
				<Stack xcss={settingsContainerStyles}>
					{loading || activationId == null ? (
						<Stack alignInline="center" space="space.300">
							<Spinner size="large" />
						</Stack>
					) : (
						<Stack>
							{fg('rovo-dev-cli-capacity-limit-reached') && !isEnabled ? (
								<Box xcss={capacityLimitContainerStyles}>
									<RovoDevCLISettingsWaitlistImage />
									<Box xcss={capacityLimitInfoSectionStyles}>
										<Heading size="medium">
											{formatMessage(messages.limitReachedInfoHeading)}
										</Heading>
										<Text as="p">{formatMessage(messages.limitReachedInfoMessage)}</Text>
										<Flex>
											<LinkButton href={JOIN_WAITLIST_LINK} appearance="primary" target="_blank">
												{formatMessage(messages.jointWaitlistButtonTitle)}
											</LinkButton>
										</Flex>
									</Box>
								</Box>
							) : (
								<Stack space="space.400">
									<Flex alignItems="center" gap="space.050">
										<Toggle
											testId="rovo-dev-cli-settings-toggle"
											size="large"
											label="Enable Rovo Dev CLI"
											onChange={handleChange}
											isChecked={isEnabled}
										/>
										<Text>{formatMessage(messages.enableToggleTitle)}</Text>
									</Flex>

									{isEnabled && <RovoDevCLIInstructions />}
								</Stack>
							)}
						</Stack>
					)}
				</Stack>
			</Stack>
		</PageSection>
	);
};

const settingsContainerStyles = xcss({
	display: 'flex',
	gap: 'space.200',
});

const capacityLimitContainerStyles = xcss({
	borderRadius: '6px',
	backgroundColor: 'color.background.neutral',
	display: 'flex',
	padding: 'space.300',
	gap: 'space.300',
	flexDirection: 'row',
});

const capacityLimitInfoSectionStyles = xcss({
	display: 'flex',
	gap: 'space.100',
	justifyContent: 'center',
});
